import { Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  buttonClasses,
  Card,
  Icon,
  linkTextClasses,
  NotProvided,
  rowLinkClasses,
  Table,
} from '@evogard/ui';
import type {
  CatalogBoleto,
  CatalogChange,
  CatalogServiceRecord,
  ChangeSubject,
  HistoryEntity,
} from '@evogard/contract';
import { canAccess, formatDate, formatInstant } from '@evogard/domain';
import { useEffect, useState, type ComponentProps } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/** A busca e a barra de filtros da tabela, repassadas como a `Table` as recebe. */
type TableChrome = Pick<ComponentProps<typeof Table>, 'search' | 'toolbar'>;
import { MoneyValue } from '../../components/Values.js';
import { api, errorMessage } from '../../lib/api.js';
import { plainText } from '../../lib/plain-text.js';
import { useSessionContext } from '../../lib/session.js';
import { PaidValue, StatusBadge } from './common.js';

/**
 * O histórico do SGA nas telas — alterações e atendimentos (14/10/2026).
 *
 * Os mesmos dois quadros aparecem em quatro lugares (as listas, e as fichas de
 * boleto, veículo e associado), e por isso moram aqui: duas cópias da coluna
 * "de → para" divergiriam no dia em que uma ganhasse um rótulo novo.
 */

/** De qual lista a alteração veio, como a tela a chama. */
// @valor — as chaves são os valores de `record_changes.subject`
export const SUBJECT_LABEL: Record<ChangeSubject, string> = {
  boleto: 'Boleto',
  associado: 'Associado',
  veiculo: 'Veículo',
};

/**
 * O nome de cada coluna do SGA que a casa acompanha — os de `CHANGE_FIELDS` na
 * esteira. Campo fora daqui aparece com o nome do SGA, que é o que ele é.
 */
// @fornecedor — as chaves são nomes de coluna do SGA
const FIELD_LABEL: Record<string, string> = {
  data_vencimento: 'Vencimento',
  data_pagamento: 'Pagamento',
  codigo_situacao: 'Situação',
  valor: 'Valor',
  email: 'E-mail',
  telefone_celular: 'Celular',
  placa: 'Placa',
  codigo_status_atendimento: 'Status do atendimento',
};

/**
 * A página de uma tabela de ficha, guardada na URL (`useTablePage`). Com ela a
 * tabela pagina no navegador, dez por página; sem ela, a tela pagina no
 * servidor e a tabela mostra tudo o que recebeu.
 */
export interface TablePaging {
  page: number;
  onPageChange: (page: number) => void;
}

/**
 * O nome que o vocabulário dá ao código — ou o CÓDIGO, dito como código.
 *
 * Mostrar "3" na coluna Departamento lê como se 3 fosse o nome. O código cru
 * aparece quando o vocabulário daquele domínio ainda não chegou à praça (a
 * passagem das listas de referência), e a tela diz que é código.
 */
export function VocabularyLabel({
  label,
  code,
  badge = false,
}: {
  label: string | null;
  code: string | null;
  badge?: boolean;
}) {
  if (label) return badge ? <Badge tone="neutro">{label}</Badge> : <>{label}</>;
  if (code) return <NotProvided>código {code}</NotProvided>;
  return <NotProvided>—</NotProvided>;
}

export function fieldLabel(field: string | null): string {
  if (field === null) return 'campo não informado';
  return FIELD_LABEL[field] ?? field;
}

/** O endereço da ficha do registro alterado, quando o núcleo o conhece. */
export function RecordLink({ change }: { change: CatalogChange }) {
  const code = change.recordCode;
  if (code === null) {
    return (
      <span className="font-mono text-sm" title="o registro ainda não chegou ao cadastro">
        {change.erpRecordCode}
      </span>
    );
  }
  const className = linkTextClasses();
  if (change.subject === 'boleto') {
    return (
      <Link to="/erp/boletos/$code" params={{ code }} className={className}>
        {code}
      </Link>
    );
  }
  if (change.subject === 'veiculo') {
    return (
      <Link to="/erp/veiculos/$code" params={{ code }} className={className}>
        {code}
      </Link>
    );
  }
  return (
    <Link to="/erp/associados/$code" params={{ code }} className={className}>
      {code}
    </Link>
  );
}

/**
 * A tabela de alterações. `withRecord` liga a coluna do registro — nas fichas
 * ela sobra, porque o registro é a própria ficha.
 */
export function ChangesTable({
  items,
  loading = false,
  withRecord = false,
  empty,
  search,
  toolbar,
  paging,
  openable = true,
}: {
  items: CatalogChange[];
  loading?: boolean;
  withRecord?: boolean;
  empty: string;
  paging?: TablePaging;
  /**
   * O botão "Ver", que abre a página de uma alteração. A própria página da
   * alteração o dispensa na tabela das outras mudanças do registro (pedido de
   * 19/10/2026): a linha já diz o campo, o de → para e quem.
   */
  openable?: boolean;
} & TableChrome) {
  return (
    <Table
      paginate={paging !== undefined}
      page={paging?.page}
      onPageChange={paging?.onPageChange}
      search={search}
      toolbar={toolbar}
      label="Alterações no ERP"
      items={items}
      loading={loading}
      rowKey={(c) => `${c.subject}-${c.erpCode}`}
      empty={empty}
      rowLink={
        openable
          ? (c) => (
              <Link
                to="/erp/alteracoes/$subject/$code"
                params={{ subject: c.subject, code: c.erpCode }}
                aria-label={`Ver a alteração ${c.erpCode}`}
                className={buttonClasses({ variant: 'contorno', size: 'sm' })}
              >
                Ver
              </Link>
            )
          : undefined
      }
      columns={[
        {
          title: 'Data',
          minWidth: '7rem',
          cell: (c) =>
            c.changedOn ? formatDate(c.changedOn) : <NotProvided>sem data</NotProvided>,
        },
        ...(withRecord
          ? [
              {
                title: 'Registro',
                minWidth: '9rem',
                cell: (c: CatalogChange) => (
                  <span className="flex items-center gap-2">
                    <Badge tone="neutro">{SUBJECT_LABEL[c.subject]}</Badge>
                    <RecordLink change={c} />
                  </span>
                ),
              },
            ]
          : []),
        { title: 'Campo', minWidth: '9rem', cell: (c) => fieldLabel(c.field) },
        {
          title: 'De → para',
          alignment: 'inicio' as const,
          minWidth: '14rem',
          cell: (c) => (
            <span>
              <span className="text-texto-suave line-through decoration-texto-suave/50">
                {valueShown(c.oldValue, c.oldLabel)}
              </span>{' '}
              →{' '}
              <strong className="font-medium">
                {valueShown(c.newValue, c.newLabel)}
              </strong>
            </span>
          ),
        },
        {
          title: 'Quem',
          minWidth: '10rem',
          cell: (c) => c.author ?? <NotProvided>não informado</NotProvided>,
        },
      ]}
    />
  );
}

/** O nome quando o código tem um, com o código ao lado; o valor cru quando não. */
function valueShown(value: string | null, label: string | null): string {
  if (value === null || value === '') return '—';
  return label === null ? value : `${label} (${value})`;
}

/**
 * O trecho da descrição que cabe na linha — três linhas, e o resto na página
 * do atendimento (o botão "Ver"). O atendimento de sinistro do SGA tem trinta
 * linhas, e a tabela inteira virava um atendimento.
 */
function Excerpt({ html }: { html: string }) {
  return (
    <span className="mt-0.5 line-clamp-3 whitespace-pre-line text-sm text-texto-suave">
      {plainText(html)}
    </span>
  );
}

/** O quadro de alterações de uma ficha, com o número no título. */
export function ChangesCard({
  changes,
  of,
  paging,
}: {
  changes: CatalogChange[];
  of: string;
  paging?: TablePaging;
}) {
  return (
    <Card
      title={`Alterações no ERP (${changes.length})`}
      descricao={`o que mudou ${of} no SGA, da mais recente para a mais antiga`}
      bleed
    >
      <ChangesTable
        items={changes}
        paging={paging}
        empty="Nenhuma alteração registrada — a esteira traz as alterações continuamente, desde o primeiro dia em que rodou."
      />
    </Card>
  );
}

/** A tabela de atendimentos. `withMember` liga a coluna do associado. */
export function ServiceRecordsTable({
  items,
  loading = false,
  withMember = false,
  empty,
  search,
  toolbar,
  paging,
}: {
  items: CatalogServiceRecord[];
  loading?: boolean;
  withMember?: boolean;
  empty: string;
  paging?: TablePaging;
} & TableChrome) {
  return (
    <Table
      paginate={paging !== undefined}
      page={paging?.page}
      onPageChange={paging?.onPageChange}
      search={search}
      toolbar={toolbar}
      label="Atendimentos no ERP"
      items={items}
      loading={loading}
      rowKey={(a) => a.erpCode ?? `${a.registeredOn}-${a.title}`}
      empty={empty}
      rowLink={(a) =>
        a.erpCode === null ? null : (
          <Link
            to="/erp/atendimentos/$code"
            params={{ code: a.erpCode }}
            aria-label={`Ver o atendimento ${a.title ?? a.erpCode}`}
            className={buttonClasses({ variant: 'contorno', size: 'sm' })}
          >
            Ver
          </Link>
        )
      }
      columns={[
        {
          title: 'Data',
          minWidth: '7rem',
          cell: (a) =>
            a.registeredOn ? (
              formatDate(a.registeredOn)
            ) : (
              <NotProvided>sem data</NotProvided>
            ),
        },
        ...(withMember
          ? [
              {
                title: 'Associado',
                minWidth: '12rem',
                cell: (a: CatalogServiceRecord) =>
                  a.member ? (
                    <Link
                      to="/erp/associados/$code"
                      params={{ code: a.member.code }}
                      className={linkTextClasses()}
                    >
                      {a.member.nome ?? a.member.code}
                    </Link>
                  ) : (
                    <NotProvided>sem associado</NotProvided>
                  ),
              },
            ]
          : []),
        {
          title: 'Atendimento',
          alignment: 'inicio' as const,
          minWidth: '18rem',
          cell: (a) => (
            <span className="block">
              <strong className="font-medium">{a.title ?? 'sem título'}</strong>
              {a.missing && (
                <Badge tone="alerta" className="ml-2">
                  saiu do SGA
                </Badge>
              )}
              {a.description && <Excerpt html={a.description} />}
            </span>
          ),
        },
        {
          title: 'Tipo',
          minWidth: '9rem',
          cell: (a) => <VocabularyLabel label={a.typeLabel} code={a.typeCode} />,
        },
        {
          title: 'Departamento',
          minWidth: '9rem',
          cell: (a) => (
            <VocabularyLabel label={a.departmentLabel} code={a.departmentCode} />
          ),
        },
        {
          title: 'Status',
          minWidth: '8rem',
          cell: (a) => (
            <VocabularyLabel label={a.statusLabel} code={a.statusCode} badge />
          ),
        },
        {
          title: 'Placa',
          minWidth: '7rem',
          cell: (a) =>
            a.vehicle ? (
              <Link
                to="/erp/veiculos/$code"
                params={{ code: a.vehicle.code }}
                className={linkTextClasses()}
              >
                {a.vehicle.placa ?? a.vehicle.code}
              </Link>
            ) : (
              <NotProvided>—</NotProvided>
            ),
        },
        {
          title: 'Valor',
          minWidth: '7rem',
          cell: (a) =>
            a.amount && Number(a.amount) !== 0 ? <MoneyValue value={a.amount} /> : '—',
        },
        {
          title: 'Quem registrou',
          minWidth: '10rem',
          cell: (a) => a.author ?? <NotProvided>não informado</NotProvided>,
        },
      ]}
    />
  );
}

/**
 * A tabela de boletos das fichas — do veículo e do associado. A ficha passa a
 * lista já na ordem em que quer mostrá-la (crescente de vencimento, desde
 * 14/10/2026).
 */
export function BoletosTable({
  label,
  items,
  empty,
  paging,
}: {
  label: string;
  items: CatalogBoleto[];
  empty: string;
  paging?: TablePaging;
}) {
  return (
    <Table
      label={label}
      items={items}
      page={paging?.page}
      onPageChange={paging?.onPageChange}
      rowKey={(b) => b.code}
      empty={empty}
      rowLink={(b) => (
        <Link
          to="/erp/boletos/$code"
          params={{ code: b.code }}
          aria-label={`Abrir o boleto ${b.code}`}
          className={rowLinkClasses}
        >
          <Icon nome="seta-direita" size={16} />
        </Link>
      )}
      columns={[
        { title: 'Código', minWidth: '6rem', cell: (b) => b.code },
        {
          title: 'Vencimento',
          minWidth: '8rem',
          cell: (b) => (b.dueDate ? formatDate(b.dueDate) : <NotProvided />),
        },
        {
          title: 'Valor',
          alignment: 'fim',
          minWidth: '7rem',
          cell: (b) => <MoneyValue value={b.value} />,
        },
        {
          title: 'Pago',
          alignment: 'fim',
          minWidth: '9rem',
          cell: (b) => <PaidValue value={b.valuePaid} source={b.paymentSource} />,
        },
        {
          title: 'Competência',
          minWidth: '7rem',
          cell: (b) => b.period ?? <NotProvided />,
        },
        {
          title: 'Situação',
          minWidth: '12rem',
          cell: (b) => <StatusBadge situacao={b.situacao} />,
        },
        {
          title: 'O ERP considera pago',
          minWidth: '9rem',
          /*
           * A coluna existe por causa da situação 5 — BAIXADO C/ DESCONTO
           * com `pago = false`. Ela fica ao lado da descrição, e não
           * dentro dela: a tela mostra os dois e não escolhe, porque
           * chamar aquilo de pago ou de aberto é mentira nas duas direções.
           */
          cell: (b) =>
            b.pago === null ? (
              <NotProvided>o ERP não disse</NotProvided>
            ) : (
              <Badge tone={b.pago ? 'sucesso' : 'neutro'}>{b.pago ? 'sim' : 'não'}</Badge>
            ),
        },
      ]}
    />
  );
}

/**
 * O "Atualizar do ERP" das listas de histórico — atendimentos e alterações.
 *
 * Roda a passagem daquela lista NA HORA e espera (`POST /erp/history-refresh`):
 * `200` é a lista já relida, `202` é a passagem ainda rodando — e a lista se
 * relê sozinha por alguns minutos. Só para quem tem `erp.sync:executar`.
 */
export function HistoryRefreshButton({
  entity,
  hint,
}: {
  entity: HistoryEntity;
  hint: string;
}) {
  const client = useQueryClient();
  const { data: context } = useSessionContext();
  const [followUntil, setFollowUntil] = useState<number | null>(null);
  const canRefresh = canAccess(context?.permissions ?? [], 'erp.sync', 'executar');

  const mutation = useMutation({
    mutationFn: async () => {
      const r = await api.erp.refreshHistory({ body: { entity } });
      if (r.status === 200 || r.status === 202) return r.body;
      throw new Error(errorMessage(r.body, 'O ERP não respondeu ao pedido.'));
    },
    onSuccess: (result) => {
      setFollowUntil(result.estado === 'em_andamento' ? Date.now() + 180_000 : null);
      void client.invalidateQueries({ queryKey: ['erp'] });
    },
  });

  /* enquanto a passagem continua, a lista se relê de cinco em cinco segundos */
  useEffect(() => {
    if (followUntil === null) return;
    const timer = setInterval(() => {
      if (Date.now() > followUntil) setFollowUntil(null);
      else void client.invalidateQueries({ queryKey: ['erp'] });
    }, 5_000);
    return () => clearInterval(timer);
  }, [followUntil, client]);

  if (!canRefresh) return null;
  const result = mutation.data;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          icon="atualizar"
        >
          {mutation.isPending ? 'Atualizando…' : 'Atualizar do ERP'}
        </Button>
        <span className="text-sm text-texto-suave">{hint}</span>
      </div>
      {mutation.isPending && (
        <Alert tone="informacao">
          Buscando no ERP agora — a lista se atualiza aqui mesmo.
        </Alert>
      )}
      {mutation.isError && <Alert tone="erro">{mutation.error.message}</Alert>}
      {result?.estado === 'atualizado' && (
        <Alert tone="sucesso">
          Atualizado do ERP: {result.records} registro(s) lido(s). {result.detail}
        </Alert>
      )}
      {result?.estado === 'em_andamento' && (
        <Alert tone="informacao">
          O ERP ainda está respondendo. O que já chegou está na lista, e ela continua se
          atualizando sozinha por alguns minutos.
        </Alert>
      )}
      {result?.estado === 'adiada' && (
        <Alert tone="alerta">
          O ERP está fora do ar para esta base, e nada foi pedido. Tente de novo depois de{' '}
          {formatInstant(result.until)}.
        </Alert>
      )}
    </>
  );
}
